import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { ArrowRight, ArrowLeft, User, Buildings, GraduationCap, HandHeart, Check } from "@phosphor-icons/react";

const types = [
  { id: "personal", label: "I have a question", desc: "For myself, therapy, mentorship or anything else.", icon: User },
  { id: "company", label: "I represent a company", desc: "Workshops, talks or team wellbeing programmes.", icon: Buildings },
  { id: "school", label: "I represent a school or institution", desc: "Career, cognitive and wellbeing programmes.", icon: GraduationCap },
  { id: "referral", label: "I want to refer someone", desc: "Refer a friend, family member or colleague.", icon: HandHeart },
];

const companyInterests = ["Employee Wellness", "Team Workshops", "Something Else"];
const schoolInterests = ["Psychology Career Workshops", "Cognitive Health Workshops", "Both"];
const refServices = ["Couch", "Forward", "Not Sure"];

export default function GetInTouch() {
  const [step, setStep] = useState(0);
  const [type, setType] = useState("");
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Deep-link support: /contact?type=referral preselects the intent.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("type");
    if (t && types.some((x) => x.id === t)) {
      setType(t);
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFiles = (e) => setFiles(Array.from(e.target.files || []).slice(0, 3));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { enquiry_type: type, ...form, message: form.message || "" };
      if (type === "referral" && files.length) {
        const uploaded = [];
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", f);
          const r = await api.post("/uploads/public-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push({ name: f.name, url: r.data.url, size: f.size });
        }
        payload.attachments = uploaded;
      }
      await api.post("/enquiries", payload);
      setSent(true);
      toast.success("Thanks, we'll get back within 2 business days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <Page>
        <section className="md-section">
          <div className="md-container max-w-xl text-center" data-testid="enquiry-thankyou">
            <div className="how-glass-ico mx-auto mb-5" style={{ background: "var(--md-bg-alt)", borderColor: "var(--md-border)" }}>
              <Check size={28} weight="bold" style={{ color: "var(--md-primary)" }} />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif">Thank you</h1>
            <p className="mt-3 text-base" style={{ color: "var(--md-text-soft)" }}>
              We&apos;ve received your message and will get back within 2 business days. Meanwhile, feel free to browse our therapist directory or session menu.
            </p>
          </div>
        </section>
      </Page>
    );
  }

  return (
    <Page>
      <Seo title="Get in Touch" description="One form, four paths. Tell us who you are and we'll get back within 2 business days." />

      <section className="md-section md-page-hero" data-testid="contact-hero">
        <div className="md-container max-w-3xl">
          <div className="md-label-eyebrow mb-3">Get in Touch</div>
          <h1 className="text-4xl md:text-5xl font-serif mb-3">One form, four paths</h1>
          <p className="text-base">Tell us who you are, the form shapes itself around your answer.</p>
        </div>
      </section>
      <section className="md-section pt-0">
        <div className="md-container max-w-3xl">
          {/* STEP 1: choose path */}
          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10" data-testid="step-type">
              {types.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setType(t.id); setForm({}); setFiles([]); setStep(1); }}
                  className="md-card md-card-hover text-left"
                  data-testid={`enquiry-type-${t.id}`}
                >
                  <t.icon size={28} weight="duotone" style={{ color: "var(--md-primary)" }} />
                  <h3 className="text-lg font-serif mt-3">{t.label}</h3>
                  <p className="text-sm mt-1.5" style={{ color: "var(--md-text-soft)" }}>{t.desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: the form changes based on the answer */}
          {step === 1 && (
            <form onSubmit={submit} className="md-card space-y-4 mt-10" data-testid="step-details">
              <Field label={type === "referral" ? "Your name" : "Name"} required>
                <input className="md-input" value={form.name || ""} onChange={(e) => update("name", e.target.value)} required data-testid="enquiry-name" />
              </Field>
              {(type === "company" || type === "school") && (
                <>
                  <Field label={type === "company" ? "Company name" : "School / institution name"} required>
                    <input className="md-input" value={form.company || ""} onChange={(e) => update("company", e.target.value)} required data-testid="enquiry-org-name" />
                  </Field>
                  <Field label="Your role" required>
                    <input className="md-input" value={form.role || ""} onChange={(e) => update("role", e.target.value)} required data-testid="enquiry-role" />
                  </Field>
                </>
              )}
              <Field label={type === "referral" ? "Your email" : "Email"} required>
                <input type="email" className="md-input" value={form.email || ""} onChange={(e) => update("email", e.target.value)} required data-testid="enquiry-email" />
              </Field>
              <Field label="Phone" required>
                <input type="tel" minLength={7} required className="md-input" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="enquiry-phone" />
              </Field>

              {type === "company" && (
                <Field label="What are you looking for?" required>
                  <select className="md-input" value={form.interest || ""} onChange={(e) => update("interest", e.target.value)} required data-testid="enquiry-interest">
                    <option value="">Choose…</option>
                    {companyInterests.map((i) => <option key={i}>{i}</option>)}
                  </select>
                </Field>
              )}

              {type === "school" && (
                <Field label="What are you interested in?" required>
                  <select className="md-input" value={form.interest || ""} onChange={(e) => update("interest", e.target.value)} required data-testid="enquiry-interest">
                    <option value="">Choose…</option>
                    {schoolInterests.map((i) => <option key={i}>{i}</option>)}
                  </select>
                </Field>
              )}

              {type === "referral" && (
                <>
                  <Field label="Referred person's name" required>
                    <input className="md-input" value={form.referred_name || ""} onChange={(e) => update("referred_name", e.target.value)} required data-testid="enquiry-referred-name" />
                  </Field>
                  <Field label="Referred person's email" required>
                    <input type="email" className="md-input" value={form.referred_email || ""} onChange={(e) => update("referred_email", e.target.value)} required data-testid="enquiry-referred-email" />
                  </Field>
                  <Field label="Which service are you referring for?" required>
                    <select className="md-input" value={form.service || ""} onChange={(e) => update("service", e.target.value)} required data-testid="enquiry-service">
                      <option value="">Choose…</option>
                      {refServices.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                </>
              )}

              <Field label={type === "referral" ? "Any additional message" : "Message"} required={type !== "referral"}>
                <textarea
                  className="md-input"
                  rows={5}
                  required={type !== "referral"}
                  placeholder="What's been on your mind? What would a good outcome look like?"
                  value={form.message || ""}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="enquiry-message"
                />
              </Field>

              {type === "referral" && (
                <Field label="Attach documents (optional, up to 3, PDF, Word or images)">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp"
                    onChange={onFiles}
                    className="block w-full text-sm"
                    style={{ color: "var(--md-text-soft)" }}
                    data-testid="enquiry-files"
                  />
                  {files.length > 0 && (
                    <ul className="mt-3 text-sm space-y-1" data-testid="enquiry-files-list">
                      {files.map((f) => (
                        <li key={f.name} className="flex items-center justify-between px-3 py-1.5 rounded" style={{ background: "var(--md-bg-alt)" }}>
                          <span style={{ color: "var(--md-text-soft)" }}>{f.name}</span>
                          <span style={{ color: "var(--md-text-muted)", fontSize: "0.75rem" }}>{(f.size / 1024).toFixed(0)} KB</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
              )}

              <label className="flex items-start gap-2 text-sm mt-2" style={{ color: "var(--md-text-soft)" }}>
                <input type="checkbox" checked={!!form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-1" required data-testid="enquiry-consent" />
                <span>I agree to be contacted by MindDhara about this enquiry. We&apos;ll never share your details.</span>
              </label>

              <div className="flex items-center justify-between pt-4">
                <button type="button" className="btn-outline text-sm" onClick={() => setStep(0)} data-testid="enquiry-back">
                  <ArrowLeft size={14} weight="bold" /> Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary text-sm"
                  style={submitting ? { opacity: 0.55, cursor: "not-allowed" } : {}}
                  data-testid="enquiry-submit"
                >
                  {submitting ? "Sending…" : "Send Enquiry"} <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </Page>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="md-label-eyebrow block mb-1.5">{label}{required && " *"}</label>
      {children}
    </div>
  );
}
